import type { CurrencyOption } from './lookups';

export interface CurrencyDef {
  code: string;
  symbol: string;
  label: string;
  /** ISO 3166-1 alpha-2 country code for flag display */
  countryCode: string;
}

/**
 * Canonical currency list used across public pages and the mobile app.
 * Single source of truth — consumed by PublicHeader, goal selectors, the mobile
 * display-currency picker, and any other UI that needs a pickable currency list.
 */
export const CURRENCY_CODES: readonly CurrencyDef[] = [
  { code: 'USD', symbol: '$', label: 'US Dollar', countryCode: 'US' },
  { code: 'EUR', symbol: '€', label: 'Euro', countryCode: 'EU' },
  { code: 'GBP', symbol: '£', label: 'British Pound', countryCode: 'GB' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar', countryCode: 'AU' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar', countryCode: 'CA' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen', countryCode: 'JP' },
  { code: 'CHF', symbol: 'Fr', label: 'Swiss Franc', countryCode: 'CH' },
  { code: 'NZD', symbol: 'NZ$', label: 'New Zealand Dollar', countryCode: 'NZ' },
  { code: 'PLN', symbol: 'zł', label: 'Polish Zloty', countryCode: 'PL' },
  { code: 'DKK', symbol: 'kr', label: 'Danish Krone', countryCode: 'DK' },
  { code: 'SEK', symbol: 'kr', label: 'Swedish Krona', countryCode: 'SE' },
  { code: 'NOK', symbol: 'kr', label: 'Norwegian Krone', countryCode: 'NO' },
  { code: 'PHP', symbol: '₱', label: 'Philippine Peso', countryCode: 'PH' },
  { code: 'MXN', symbol: 'MX$', label: 'Mexican Peso', countryCode: 'MX' },
  { code: 'BRL', symbol: 'R$', label: 'Brazilian Real', countryCode: 'BR' },
  { code: 'ARS', symbol: 'AR$', label: 'Argentine Peso', countryCode: 'AR' },
  { code: 'CLP', symbol: 'CL$', label: 'Chilean Peso', countryCode: 'CL' },
  { code: 'COP', symbol: 'CO$', label: 'Colombian Peso', countryCode: 'CO' },
  { code: 'GTQ', symbol: 'Q', label: 'Guatemalan Quetzal', countryCode: 'GT' },
  { code: 'VND', symbol: '₫', label: 'Vietnamese Dong', countryCode: 'VN' },
  { code: 'CZK', symbol: 'Kč', label: 'Czech Koruna', countryCode: 'CZ' },
  { code: 'HUF', symbol: 'Ft', label: 'Hungarian Forint', countryCode: 'HU' },
  { code: 'RON', symbol: 'lei', label: 'Romanian Leu', countryCode: 'RO' },
  { code: 'PKR', symbol: '₨', label: 'Pakistani Rupee', countryCode: 'PK' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee', countryCode: 'IN' },
  { code: 'KRW', symbol: '₩', label: 'South Korean Won', countryCode: 'KR' },
  { code: 'MYR', symbol: 'RM', label: 'Malaysian Ringgit', countryCode: 'MY' },
  { code: 'THB', symbol: '฿', label: 'Thai Baht', countryCode: 'TH' },
  { code: 'IDR', symbol: 'Rp', label: 'Indonesian Rupiah', countryCode: 'ID' },
  { code: 'SGD', symbol: 'S$', label: 'Singapore Dollar', countryCode: 'SG' },
  { code: 'HKD', symbol: 'HK$', label: 'Hong Kong Dollar', countryCode: 'HK' },
  { code: 'TWD', symbol: 'NT$', label: 'Taiwan Dollar', countryCode: 'TW' },
  { code: 'TRY', symbol: '₺', label: 'Turkish Lira', countryCode: 'TR' },
  { code: 'MKD', symbol: 'ден', label: 'Macedonian Denar', countryCode: 'MK' },
  { code: 'RSD', symbol: 'дин', label: 'Serbian Dinar', countryCode: 'RS' },
  { code: 'ZAR', symbol: 'R', label: 'South African Rand', countryCode: 'ZA' },
] as const;

/** CURRENCY_CODES as CurrencyOption[] (code, symbol, label) for UI dropdowns */
export const CURRENCY_OPTIONS: CurrencyOption[] = CURRENCY_CODES.map(({ code, symbol, label }) => ({
  code,
  symbol,
  label,
}));

/** Look up a currency symbol by code, defaults to '$' */
export function getCurrencySymbol(code: string): string {
  return CURRENCY_CODES.find((c) => c.code === code)?.symbol ?? '$';
}

/** Look up a currency symbol by code, falling back to the code itself */
export function getCurrencySymbolOrCode(code: string): string {
  return CURRENCY_CODES.find((c) => c.code === code)?.symbol ?? code;
}

export const defaultCurrency = 'USD';

/**
 * Map ISO country codes to currencies (geolocation / profile country fallback).
 * Countries not in this map default to `defaultCurrency`.
 */
export const countryToCurrency: Record<string, string> = {
  // Eurozone
  DE: 'EUR',
  AT: 'EUR',
  FR: 'EUR',
  BE: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  LU: 'EUR',
  MC: 'EUR',
  LI: 'EUR',

  GB: 'GBP',
  CH: 'CHF',
  AU: 'AUD',
  NZ: 'NZD',
  CA: 'CAD',
  JP: 'JPY',
};

/** Get currency from country code with fallback to `defaultCurrency` */
export function getCurrencyFromCountry(countryCode: string | null | undefined): string {
  if (!countryCode) return defaultCurrency;
  return countryToCurrency[countryCode.toUpperCase()] ?? defaultCurrency;
}
